/**
 * Cienki klient API uwierzytelnienia (allauth.headless, klient browser).
 * Pisany ręcznie — spec allauth (/_allauth/openapi.yaml) używa unii anyOf,
 * których generatory klientów nie obsługują dobrze; API domenowe nadal
 * generuje orval z openapi/schema.yaml.
 */

const BASE = '/_allauth/browser/v1'

export type SessionUser = {
  id: number
  display: string
  email?: string
  username?: string
}

export type AuthFlow = {
  id: string
  is_pending?: boolean
  types?: string[]
}

export type AuthResponse = {
  status: number
  data?: {
    user?: SessionUser
    flows?: AuthFlow[]
  }
  meta?: {
    is_authenticated?: boolean
    /** Tylko odpowiedź GET 2fa/enroll (świeży, niezatwierdzony sekret). */
    secret?: string
    totp_url?: string
  }
  errors?: { message: string; code: string; param?: string }[]
}

export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Backend nazywa cookie CSRF przez env: wdrożenia używają nazwy z prefiksem,
 * bo cookie na całą domenę zderzało się z `csrftoken` innych aplikacji Django
 * pod tą samą domeną; lokalnie zostaje domyślna nazwa Django.
 */
const CSRF_COOKIE_NAMES = ['finpath_csrftoken', 'csrftoken']

export function getCsrfToken(): string | null {
  for (const name of CSRF_COOKIE_NAMES) {
    const value = getCookie(name)
    if (value) return value
  }
  return null
}

/**
 * Zimne wejście (np. prosto z linku w mailu) nie ma jeszcze cookie CSRF —
 * każdy GET na endpoint browser je ustawia, więc dosypujemy je przed mutacją.
 */
export async function ensureCsrfCookie(): Promise<void> {
  if (!getCsrfToken()) {
    await fetch(`${BASE}/auth/session`, { credentials: 'include' })
  }
}

async function call<T = AuthResponse>(
  method: string,
  path: string,
  body?: unknown,
  headers?: Record<string, string>,
): Promise<T> {
  if (method !== 'GET') {
    await ensureCsrfCookie()
  }
  const response = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(method !== 'GET' ? { 'X-CSRFToken': getCsrfToken() ?? '' } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  // Każda odpowiedź headless niesie JSON ze statusem; fallback dla np. 502.
  return (await response.json().catch(() => ({ status: response.status }))) as T
}

export const authApi = {
  /** Stan sesji; przy okazji ustawia cookie CSRF (robi to każdy endpoint browser). */
  getSession: () => call('GET', '/auth/session'),
  login: (credentials: { email?: string; username?: string; password: string }) =>
    call('POST', '/auth/login', credentials),
  /** Drugi krok logowania (TR-113) — kod TOTP przy pending flow mfa_authenticate. */
  mfaAuthenticate: (code: string) => call('POST', '/auth/2fa/authenticate', { code }),
  /** Wymuszony enrollment (TR-111, pending flow mfa_enroll): GET = świeży sekret (404). */
  getTotpEnroll: () => call('GET', '/auth/2fa/enroll'),
  /** Aktywacja TOTP kodem; sukces domyka logowanie (pełna sesja). */
  activateTotpEnroll: (code: string) => call('POST', '/auth/2fa/enroll', { code }),
  /** Wymuszona zmiana hasła na sesji ograniczonej (TR-125, flow password_change_required). */
  changeRequiredPassword: (password: string) =>
    call('POST', '/auth/password/change-required', { password }),
  /** TR-1002 R5: obowiązkowy setup PIN-u i pytania na częściowej sesji browser. */
  setupStudentRecovery: (pin: string, questionId: string, answer: string) =>
    call('POST', '/auth/recovery/setup', {
      pin,
      question_id: questionId,
      answer,
    }),
  /** TR-120: zmiana hasła z profilu; bieżąca sesja zostaje, pozostałe giną. */
  changePassword: (currentPassword: string, newPassword: string) =>
    call('POST', '/account/password/change', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  /** Wylogowanie; unieważnia też sesję częściową (pending 2FA). Odpowiada 401. */
  logout: () => call('DELETE', '/auth/session'),
  /** Zamówienie linku resetu (TR-121); 200 niezależnie od istnienia konta. */
  requestPassword: (email: string) => call('POST', '/auth/password/request', { email }),
  /** Walidacja klucza z linku w mailu; 200 = klucz dobry. */
  getPasswordReset: (key: string) =>
    call('GET', '/auth/password/reset', undefined, { 'X-Password-Reset-Key': key }),
  /** Ustawienie nowego hasła; sukces nie loguje (odpowiedź 401 bez errors). */
  resetPassword: (key: string, password: string) =>
    call('POST', '/auth/password/reset', { key, password }),
}

export function hasPendingFlow(response: AuthResponse, flowId: string): boolean {
  return (
    response.status === 401 &&
    (response.data?.flows ?? []).some((flow) => flow.id === flowId && flow.is_pending)
  )
}

/** True when the response carries an allauth validation error with the given `code`. */
export function hasErrorCode(response: AuthResponse, code: string): boolean {
  return (response.errors ?? []).some((error) => error.code === code)
}

/**
 * True when the login attempt was throttled rather than simply wrong.
 *
 * allauth shares one `login_failed` rate-limit pool across the password step and
 * the 2FA step; once exhausted it raises `too_many_login_attempts` as a plain
 * *validation error* (HTTP 400, `errors[].code`), NOT an HTTP 429 — so the code,
 * not the status, is the reliable signal. The 429 branch is a belt-and-suspenders
 * fallback for a throttle applied further out (proxy/gateway), which would not
 * carry the allauth error body. The response exposes no unblock time, so callers
 * must not invent one.
 */
export function isRateLimited(response: AuthResponse): boolean {
  return response.status === 429 || hasErrorCode(response, 'too_many_login_attempts')
}
